import { useCallback, useMemo, useState } from 'react';
import { timeAndLeaveGateway } from '../index';
import type { EscoHrLeaveRequestResponse, EscoHrWorkforceSummaryResponse } from '../types';
import { RetryableError } from '../../../shared/RetryableError';
import { usePermissionCheck } from '../../identity/state/usePermissions';
import { EscoHrConnectionBanner, SyntheticBadge } from './escoHrWorkforceShared';
import { hasEscoHrData, humanize, useEmployeeNames, useEscoHrLoad } from './escoHrWorkforceHooks';

const LEAVE_REQUESTS_READ = 'leaverequests.read';

interface TimeOffData {
  summary: EscoHrWorkforceSummaryResponse;
  requests: EscoHrLeaveRequestResponse[];
}

/**
 * Time off from escoHR, read-only. escoHR is authoritative for the request, its approval and the balance; EPN never approves or edits anything
 * here, and nothing here is used in payroll. Only approved, reviewed leave could ever matter to payroll, in a later, separately approved change.
 * Reasons are codes, never free text; there are no documents and no medical detail.
 */
export function EscoHrTimeOffSection() {
  const can = usePermissionCheck();
  const allowed = can(LEAVE_REQUESTS_READ);
  const load = useCallback(async (signal: AbortSignal): Promise<TimeOffData> => {
    const [summary, requests] = await Promise.all([
      timeAndLeaveGateway.getEscoHrTimeOffSummary(signal),
      timeAndLeaveGateway.listEscoHrLeaveRequests(signal),
    ]);
    return { summary, requests };
  }, []);
  const { data, error, reload } = useEscoHrLoad(allowed, load);
  const employeeName = useEmployeeNames(allowed && data !== null);

  const [employeeId, setEmployeeId] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');

  const requests = data?.requests;
  const visible = useMemo(() => (requests ?? []).filter((r) =>
    (!employeeId || r.epnEmployeeId === employeeId)
    && (!status || r.status === status)
    && (!search || (r.leaveTypeRef ?? '').toLowerCase().includes(search.trim().toLowerCase()))), [requests, employeeId, status, search]);

  if (!allowed) {
    return <section className="card card--wide"><p className="muted">You do not have permission to view time off from escoHR.</p></section>;
  }

  if (error) {
    return <section className="card card--wide" data-testid="escohr-timeoff-error"><RetryableError message={error} onRetry={reload} /></section>;
  }

  if (!data) {
    return <section className="card card--wide" aria-busy="true"><p className="muted">Loading time off from escoHR…</p></section>;
  }

  const employeeIds = [...new Set(data.requests.map((r) => r.epnEmployeeId))];
  const statuses = [...new Set(data.requests.map((r) => r.status))].sort();

  return (
    <div data-testid="escohr-timeoff">
      <EscoHrConnectionBanner summary={data.summary} heading="escoHR Time Off" />
      {hasEscoHrData(data.summary) && (
        <section className="card card--wide" aria-label="escoHR time-off requests">
          <h4>Time-off requests from escoHR</h4>
          <p className="muted">
            escoHR owns the request, its approval and the balance; this is a read-only copy. Nothing here is used in payroll, and EPN does not approve or change these requests.
          </p>

          {data.requests.length > 0 && (
            <div className="inline-form">
              <select aria-label="Filter by employee" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
                <option value="">All employees</option>
                {employeeIds.map((id) => <option key={id} value={id}>{employeeName(id)}</option>)}
              </select>
              <select aria-label="Filter by status" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All statuses</option>
                {statuses.map((s) => <option key={s} value={s}>{humanize(s)}</option>)}
              </select>
              <input aria-label="Search leave type" placeholder="Search leave type" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          )}

          <div style={{ overflowX: 'auto', contain: 'inline-size' }}>
            <table aria-label="escoHR time-off requests">
              <thead>
                <tr><th>Employee</th><th>Leave type</th><th>Dates</th><th>Requested</th><th>Approved</th><th>Status</th><th></th></tr>
              </thead>
              <tbody>
                {visible.map((r) => (
                  <tr key={r.id}>
                    <td>{employeeName(r.epnEmployeeId)}</td>
                    <td>{r.leaveTypeRef ?? '—'}</td>
                    <td>{r.firstDate ?? '—'}{r.lastDate && r.lastDate !== r.firstDate ? ` to ${r.lastDate}` : ''}</td>
                    <td>{r.requestedHours} h</td>
                    <td>{r.approvedHours === null ? '—' : `${r.approvedHours} h`}</td>
                    <td>
                      {humanize(r.status)}
                      {r.reasonCode && <div className="muted">Reason: {humanize(r.reasonCode)}</div>}
                    </td>
                    <td>
                      {r.partial && <div className="muted">Partly approved</div>}
                      {r.emergency && <div className="muted">Unplanned</div>}
                      {r.requiresHrReview && <div className="muted">Needs HR review</div>}
                      {r.isSynthetic && <SyntheticBadge />}
                    </td>
                  </tr>
                ))}
                {data.requests.length === 0 && <tr><td colSpan={7} className="muted">No time-off requests have been received from escoHR yet.</td></tr>}
                {data.requests.length > 0 && visible.length === 0 && <tr><td colSpan={7} className="muted">No requests match these filters.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
